import React, { memo } from "react"
import clsx from "clsx"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Chip from "./Chip"
import { usePlanning } from "./PlanningContext"
import useDropTarget from "../hooks/useDropTarget"
import { formatHours, sumHours } from "../lib/hours"

const ACCEPTS = ["project", "assignment"]

const AbsenceTile = ({ absence, density }) => {
  const label = absence.label || I18n.t(absence.task ? "project_planning.task" : "project_planning.absence")
  return (
    <div
      title={[label, absence.times].filter(Boolean).join("\n")}
      className={clsx(
        "rounded-[5px] px-1.5 text-white select-none",
        absence.task ? "bg-slate-700" : "bg-slate-400",
        density === "compact" ? "py-0.5" : "py-1",
      )}
    >
      {density !== "compact" && (
        <div className="flex items-center gap-1 text-[10px] font-medium leading-[1.25] opacity-90">
          <FontAwesomeIcon icon={absence.task ? "clipboard-check" : "calendar-xmark"} className="text-[9px]" />
          <span className="truncate">{absence.times || I18n.t("project_planning.all_day")}</span>
        </div>
      )}
      <div className={clsx("flex items-center gap-1 truncate font-semibold leading-[1.25]", density === "compact" ? "text-[10.5px]" : "text-[11.5px]")}>
        {density === "compact" && <FontAwesomeIcon icon={absence.task ? "clipboard-check" : "calendar-xmark"} className="text-[9px] shrink-0" />}
        <span className="truncate">{label}</span>
      </div>
    </div>
  )
}

const Cell = ({ userId, date, tone, items, absence, selectedKey, inRange }) => {
  const { handlers, settings, density, canWrite } = usePlanning()
  const blocked  = Boolean(absence?.all_day)
  const { over, dropProps } = useDropTarget({
    accepts: ACCEPTS,
    blocked,
    onDrop: (drag, altKey) => handlers.dropOnPerson({ userId, date, drag, altKey }),
  })
  const total    = sumHours(items)
  const overMax  = userId && settings.daily_max > 0 && total > settings.daily_max
  const selected = selectedKey ? selectedKey.split(",") : []

  return (
    <div
      {...(canWrite ? dropProps : {})}
      data-cell={`${userId || "none"}|${date}`}
      onMouseDown={canWrite ? event => handlers.rangeStart({ userId, date, event }) : undefined}
      onMouseEnter={canWrite ? event => handlers.rangeExtend({ userId, date, event }) : undefined}
      onClick={canWrite ? event => handlers.clickCell({ userId, date, event }) : undefined}
      className={clsx(
        "pp-drop group/cell relative flex flex-col gap-1 p-1 border-r border-b border-slate-100 transition-colors",
        density === "compact" ? "min-h-[46px]" : "min-h-[54px]",
        tone,
        blocked && "pp-blocked pp-hatch",
        canWrite && !blocked && "cursor-cell",
        inRange && "bg-brand-100/70 ring-2 ring-inset ring-brand-400",
        over === "ok" && "pp-over bg-brand-50 ring-2 ring-inset ring-brand-400",
        over === "blocked" && "bg-rose-50 ring-2 ring-inset ring-rose-300 cursor-not-allowed",
        overMax && !over && "ring-2 ring-inset ring-rose-400",
      )}
    >
      {absence && <AbsenceTile absence={absence} density={density} />}

      {items.map(item => <Chip key={item.id} assignment={item} selected={selected.includes(String(item.id))} />)}

      {overMax && (
        <span
          title={I18n.t("project_planning.over_max", { total: formatHours(total), max: formatHours(settings.daily_max) })}
          className="absolute -top-1 -right-1 z-[2] grid place-items-center w-4 h-4 rounded-full bg-rose-500 text-white text-[10px] font-bold"
        >
          !
        </span>
      )}

      {canWrite && !blocked && items.length === 0 && !inRange && !over && (
        <span className="pp-drop-hint absolute inset-0 grid place-items-center text-slate-300 opacity-0 group-hover/cell:opacity-100 transition-opacity pointer-events-none">
          <FontAwesomeIcon icon="plus" className="text-[11px]" />
        </span>
      )}
    </div>
  )
}

export default memo(Cell)
